/* eslint-disable @typescript-eslint/no-unsafe-return */
import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { API_ACTION, API_SCOPE, ApiAction, ApiScope } from 'src/app/shared/interceptor/interceptor.token';
import { CommonApiResponse } from 'src/app/shared/interface/common.interface';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { environment as ENV } from 'src/environments/environment';
import {
  ExtendedPropertyApiDetail,
  GenerateListingSheetApiRequest,
  PropertyDataApiRequest,
  PropertyPageView,
  PropertyViewApiRequest,
  PropertyViewApiResponse,
  UnitDetail,
} from '../interface/property.interface';

@Injectable({
  providedIn: 'root',
})
export class PropertyApi {
  constructor(
    private _http: HttpClient,
    private snackBarService: SnackBarService
  ) {}

  getActivePropertyPageView(params: PropertyViewApiRequest): Observable<PropertyPageView> {
    const httpParams: any = { ...params };
    return this._http
      .get<CommonApiResponse<PropertyViewApiResponse>>(`${ENV.backendBaseUrl}/api/getPageLayoutDetails`, {
        params: httpParams,
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data.pageLayout),
        catchError((error: HttpErrorResponse) => {
          // const errorMessage = error.error.message;
          // this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  getActivePropertyPageData(params: PropertyDataApiRequest): Observable<ExtendedPropertyApiDetail> {
    const httpParams: any = { ...params };
    return this._http
      .get<CommonApiResponse<ExtendedPropertyApiDetail>>(`${ENV.backendBaseUrl}/api/getPropertyDetails`, {
        params: httpParams,
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /**
   * Fetching street view or satellite view
   * @param url
   * @param params
   * @returns
   */
  getStreetOrSatelliteView(url: string, params: any) {
    const httpParams: any = {
      ...params,
    };

    return this._http
      .get(url, {
        params: httpParams,
        responseType: 'blob',
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  initPropertyPdfGeneration(params: GenerateListingSheetApiRequest): Observable<any> {
    let httpParams = new HttpParams()
      .set('api', '/reports/v1/GenerateListingSheet')
      .set('authType', 'app_static')
      .set('apiType', 'data');

    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        httpParams = httpParams.set(key, value);
      }
    }

    return this._http.get<CommonApiResponse<any>>(`${ENV.backendBaseUrl}/api/proxy/get`, {
      params: httpParams,
      context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
    });
  }

  fetchGeneratedPropertyPdf(referenceId: number): Observable<any> {
    const params = new HttpParams()
      .set('api', '/reports/v1/GetListingSheet')
      .set('authType', 'app_static')
      .set('apiType', 'data')
      .set('ReferenceId', referenceId);

    return this._http.get<CommonApiResponse<any>>(`${ENV.backendBaseUrl}/api/proxy/get`, {
      params,
      context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
    });
  }

  /** Add a structure */
  addStructure(scenarioId: string, structure: { name?: string; structureType?: string }): Observable<boolean> {
    return this._http
      .post<CommonApiResponse<void>>(
        `${ENV.backendBaseUrl}/api/addStructure`,
        { scenarioId, structure, addToAllValuationTypes: true },
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.open('error', error.error.message);
          return of(false);
        })
      );
  }

  /** Adds `count` units to a structure and returns that structure's full units array. */
  addUnit(
    scenarioId: string | undefined,
    structureId: number,
    propertyId: string,
    count: number
  ): Observable<UnitDetail[] | null> {
    return this._http
      .post<CommonApiResponse<{ units: UnitDetail[] }>>(
        `${ENV.backendBaseUrl}/api/addUnit`,
        { structureId, propertyId, count, ...(scenarioId ? { scenarioId, addToAllValuationTypes: true } : {}) },
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res.data?.units ?? null),
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.open('error', error.error.message);
          return of(null);
        })
      );
  }

  /** Delete a structure from a property */
  deleteStructure(scenarioId: string, structureId: number): Observable<boolean> {
    return this._http
      .post<CommonApiResponse<void>>(
        `${ENV.backendBaseUrl}/api/removeStructure`,
        { scenarioId, structureId, addToAllValuationTypes: true },
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.open('error', error.error.message);
          return of(false);
        })
      );
  }

  /** Removes the given units from a structure. */
  removeUnits(
    scenarioId: string | undefined,
    structureId: number,
    propertyId: string,
    unitIds: number[]
  ): Observable<boolean> {
    return this._http
      .post<CommonApiResponse<void>>(
        `${ENV.backendBaseUrl}/api/removeUnit`,
        { structureId, propertyId, unitIds, ...(scenarioId ? { scenarioId, addToAllValuationTypes: true } : {}) },
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.open('error', error.error.message);
          return of(false);
        })
      );
  }
}
